// src/components/metrics/LiveMetricsBoard.jsx
import React, { useState, useMemo, useRef, useEffect } from 'react';
import ToggleGroup from '../common/ToggleGroup';
import Legend from '../common/Legend';
import { useHourMetrics } from '../../hooks/useHourMetrics';
import { useStopMetrics } from '../../hooks/useStopMetrics';
import * as londonApi from '@api/london';
import { formatNumber, formatPercent } from '@core/domain/format';
import { statusBelowOrEqual, statusAboveOrEqual } from '@core/domain/status';
import {
  buildHourLookup,
  buildStopLookup,
  buildTimingPoints,
  collectHours,
  computeDailyAggregates,
} from '@core/adapters/LondonMetrics';

const STATUS_COLORS = {
  good: 'var(--live-green)',
  bad: 'var(--live-red)',
  unknown: 'var(--text-muted)',
};

// Layout tokens — defined in index.css for per-client tuning.
const HOUR_COL_WIDTH = 'var(--metrics-hour-col-width)';
const METRIC_COL_WIDTH = 'var(--metrics-metric-col-width)';
const STOP_COL_WIDTH = 'var(--metrics-stop-col-width)';

export default function LiveMetricsBoard({ routeId, stops }) {
  // ---- Filters (persisted per route) ----
  const [directionFilter, setDirectionFilter] = useState(() => {
    if (!routeId) return 'all';
    return localStorage.getItem(`metricsDirectionFilter:${routeId}`) || 'all';
  });
  const [metricMode, setMetricMode] = useState(() => {
    if (!routeId) return 'both';
    return localStorage.getItem(`metricsMetricMode:${routeId}`) || 'both';
  });
  const [apply30Rule, setApply30Rule] = useState(() => {
    if (!routeId) return false;
    const saved = localStorage.getItem(`metricsApply30Rule:${routeId}`);
    return saved !== null ? saved === 'true' : false;
  });

  // ---- Thresholds (persisted per route) ----
  const [showSettings, setShowSettings] = useState(false);
  const [ewtThreshold, setEwtThreshold] = useState(() => {
    if (!routeId) return 1;
    const saved = localStorage.getItem(`metricsEwtThreshold:${routeId}`);
    return saved !== null ? parseFloat(saved) : 1;
  });
  const [otpThreshold, setOtpThreshold] = useState(() => {
    if (!routeId) return 75;
    const saved = localStorage.getItem(`metricsOtpThreshold:${routeId}`);
    return saved !== null ? parseFloat(saved) : 75;
  });

  // ---- PJF data ----
  const [pjfData, setPjfData] = useState({});

  useEffect(() => {
    londonApi.fetchPjf()
      .then(data => setPjfData(data))
      .catch(err => console.error('Failed to load PJF:', err));
  }, []);

  // ---- Persist state ----
  useEffect(() => {
    if (routeId) {
      localStorage.setItem(`metricsDirectionFilter:${routeId}`, directionFilter);
    }
  }, [directionFilter, routeId]);

  useEffect(() => {
    if (routeId) {
      localStorage.setItem(`metricsMetricMode:${routeId}`, metricMode);
    }
  }, [metricMode, routeId]);

  useEffect(() => {
    if (routeId) {
      localStorage.setItem(`metricsApply30Rule:${routeId}`, String(apply30Rule));
    }
  }, [apply30Rule, routeId]);

  useEffect(() => {
    if (routeId) {
      localStorage.setItem(`metricsEwtThreshold:${routeId}`, String(ewtThreshold));
    }
  }, [ewtThreshold, routeId]);

  useEffect(() => {
    if (routeId) {
      localStorage.setItem(`metricsOtpThreshold:${routeId}`, String(otpThreshold));
    }
  }, [otpThreshold, routeId]);

  // ---- Restore state on route change ----
  useEffect(() => {
    if (routeId) {
      const savedDir = localStorage.getItem(`metricsDirectionFilter:${routeId}`);
      if (savedDir) setDirectionFilter(savedDir);
      const savedMode = localStorage.getItem(`metricsMetricMode:${routeId}`);
      if (savedMode) setMetricMode(savedMode);
      const savedRule = localStorage.getItem(`metricsApply30Rule:${routeId}`);
      if (savedRule !== null) setApply30Rule(savedRule === 'true');
      const savedEwt = localStorage.getItem(`metricsEwtThreshold:${routeId}`);
      if (savedEwt !== null) setEwtThreshold(parseFloat(savedEwt));
      const savedOtp = localStorage.getItem(`metricsOtpThreshold:${routeId}`);
      if (savedOtp !== null) setOtpThreshold(parseFloat(savedOtp));
    }
  }, [routeId]);

  // ---- Fetch data ----
  const { data: hourData, loading: hourLoading } = useHourMetrics(routeId, { refetchInterval: 60000 });
  const { data: stopData, loading: stopLoading } = useStopMetrics(routeId, { refetchInterval: 60000 });

  const hasData = hourData.length > 0 || stopData.length > 0;
  const showSpinner = (hourLoading || stopLoading) && !hasData;

  // ---- Build timing point list ----
  const timingPoints = useMemo(
    () => buildTimingPoints(stops, directionFilter),
    [stops, directionFilter]
  );

  // ---- Lookups ----
  const hourLookup = useMemo(
    () => buildHourLookup(hourData, apply30Rule),
    [hourData, apply30Rule]
  );

  const stopLookup = useMemo(
    () => buildStopLookup(stopData, apply30Rule),
    [stopData, apply30Rule]
  );

  // ---- Hours ----
  const hours = useMemo(
    () => collectHours(hourLookup, stopLookup),
    [hourLookup, stopLookup]
  );

  // ---- Colour helpers ----
  const getEwtColor = (value) => STATUS_COLORS[statusBelowOrEqual(value, ewtThreshold)];
  const getOtpColor = (value) => STATUS_COLORS[statusAboveOrEqual(value, otpThreshold / 100)];

  // ---- Helper to render a single metric (EWT, OTP, or both) ----
  const renderMetricValue = (ewt, otp) => {
    if (metricMode === 'ewt') {
      return <span style={{ fontWeight: 500, color: getEwtColor(ewt) }}>{formatNumber(ewt)}</span>;
    }
    if (metricMode === 'otp') {
      return <span style={{ fontWeight: 500, color: getOtpColor(otp) }}>{formatPercent(otp)}</span>;
    }
    // both
    return (
      <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <div style={{ flex: 1, textAlign: 'center', color: getEwtColor(ewt), fontWeight: 500 }}>
          {formatNumber(ewt)}
        </div>
        <div style={{
          width: 1,
          height: '60%',
          background: 'var(--border-subtle)',
          flexShrink: 0,
        }} />
        <div style={{ flex: 1, textAlign: 'center', color: getOtpColor(otp), fontWeight: 500 }}>
          {formatPercent(otp)}
        </div>
      </div>
    );
  };

  // ---- Daily aggregates (with fallback) ----
  const dailyAggregates = useMemo(
    () => computeDailyAggregates(hourData, pjfData, apply30Rule),
    [hourData, pjfData, apply30Rule]
  );

  // ---- Render helpers for hour and stop metrics ----
  const renderHourMetric = (hour) => {
    const record = hourLookup[hour];
    if (!record) return <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>;
    return renderMetricValue(record.ewt, record.otp);
  };

  const renderStopMetric = (hour, stop) => {
    const key = `${stop.naptan_id}-${stop.direction}`;
    const record = stopLookup[hour]?.[key];
    if (!record) return <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>;
    return renderMetricValue(record.ewt, record.otp);
  };

  // ---- Styles ----
  const thBase = {
    padding: '9px 2px',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--text-muted)',
    background: 'var(--bg-surface)',
    whiteSpace: 'nowrap',
    borderBottom: '2px solid var(--border)',
    position: 'sticky',
    top: 0,
    zIndex: 20,
  };

  const stickyFirstColStyle = {
    ...thBase,
    left: 0,
    zIndex: 30,
    minWidth: HOUR_COL_WIDTH,
    width: HOUR_COL_WIDTH,
    maxWidth: HOUR_COL_WIDTH,
    textAlign: 'center',
  };

  const stickySecondColStyle = {
    ...thBase,
    left: HOUR_COL_WIDTH,
    zIndex: 25,
    minWidth: METRIC_COL_WIDTH,
    width: METRIC_COL_WIDTH,
    maxWidth: METRIC_COL_WIDTH,
    textAlign: 'center',
  };

  const stopThStyle = {
    ...thBase,
    minWidth: STOP_COL_WIDTH,
    width: STOP_COL_WIDTH,
    maxWidth: STOP_COL_WIDTH,
    textAlign: 'center',
    whiteSpace: 'normal',
    padding: '9px 6px',
  };

  const bodyCellStyle = {
    textAlign: 'center',
    padding: 'var(--metrics-cell-padding)',
    border: '1px solid var(--border-subtle)',
    fontSize: 'var(--metrics-cell-font-size)',
    fontFamily: 'var(--font-mono)',
  };

  const stickyFirstTdStyle = (rowBg) => ({
    ...bodyCellStyle,
    position: 'sticky',
    left: 0,
    background: rowBg,
    zIndex: 10,
    minWidth: HOUR_COL_WIDTH,
    width: HOUR_COL_WIDTH,
    maxWidth: HOUR_COL_WIDTH,
  });

  const stickySecondTdStyle = (rowBg) => ({
    ...bodyCellStyle,
    position: 'sticky',
    left: HOUR_COL_WIDTH,
    background: rowBg,
    zIndex: 5,
    minWidth: METRIC_COL_WIDTH,
    width: METRIC_COL_WIDTH,
    maxWidth: METRIC_COL_WIDTH,
  });

  // ---- Button style (matching ToggleGroup) ----
  const toggleBtnStyle = (isActive) => ({
    padding: '5px 14px',
    borderRadius: 7,
    border: 'none',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    transition: 'all 0.15s',
    background: isActive ? 'var(--bg-surface)' : 'transparent',
    color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
    boxShadow: isActive ? 'var(--shadow-sm)' : 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
    height: 'auto',
  });

  // ---- Scroll persistence ----
  const tableWrapRef = useRef(null);

  useEffect(() => {
    const el = tableWrapRef.current;
    if (!el || !routeId) return;
    const savedScroll = localStorage.getItem(`metricsScroll:${routeId}`);
    if (savedScroll) {
      el.scrollTop = parseInt(savedScroll, 10);
    }
    const handleScroll = () => {
      localStorage.setItem(`metricsScroll:${routeId}`, String(el.scrollTop));
    };
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [routeId]);

  useEffect(() => {
    if (hourData.length > 0 || stopData.length > 0) {
      const el = tableWrapRef.current;
      if (el && routeId) {
        const saved = localStorage.getItem(`metricsScroll:${routeId}`);
        if (saved) el.scrollTop = parseInt(saved, 10);
      }
    }
  }, [hourData, stopData, routeId]);

  if (showSpinner) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', flex: 1, color: 'var(--text-muted)' }}>
        Loading metrics…
      </div>
    );
  }

  // ---- Main render ----
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Filter bar */}
      <div style={{
        padding: '12px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        flexWrap: 'wrap',
        borderBottom: '1px solid var(--border-subtle)',
        flexShrink: 0,
      }}>
        {/* Direction toggle */}
        <ToggleGroup
          options={[
            { value: 'all', label: 'All' },
            { value: 'outbound', label: 'Outbound', dot: 'var(--ob-track)' },
            { value: 'inbound', label: 'Inbound', dot: 'var(--ib-track)' },
          ]}
          active={directionFilter}
          onChange={setDirectionFilter}
        />

        <div style={{ width: 1, height: 22, background: 'var(--border)', flexShrink: 0 }} />

        {/* Metric mode toggle */}
        <ToggleGroup
          options={[
            { value: 'both', label: 'EWT + OTP' },
            { value: 'ewt', label: 'EWT' },
            { value: 'otp', label: 'OTP' },
          ]}
          active={metricMode}
          onChange={setMetricMode}
        />

        <div style={{ width: 1, height: 22, background: 'var(--border)', flexShrink: 0 }} />

        {/* 30% Rule toggle */}
        <ToggleGroup
          options={[
            { value: false, label: 'Standard' },
            { value: true, label: '30% Rule' },
          ]}
          active={apply30Rule}
          onChange={setApply30Rule}
        />

        {/* Refresh indicator */}
        {(hourLoading || stopLoading) && hasData && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: 'var(--accent)',
              display: 'inline-block',
              animation: 'liveBlip 1s ease-in-out infinite',
            }} />
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Refreshing…</span>
          </div>
        )}

        <div style={{ flex: 1 }} />

        {/* Legend */}
        <Legend rows={[
          { label: 'EWT', items: [
            { color: 'var(--live-green)', text: `≤${ewtThreshold.toFixed(1)}m` },
            { color: 'var(--live-red)',   text: `>${ewtThreshold.toFixed(1)}m` },
          ]},
          { label: 'OTP', items: [
            { color: 'var(--live-green)', text: `≥${otpThreshold.toFixed(1)}%` },
            { color: 'var(--live-red)',   text: `<${otpThreshold.toFixed(1)}%` },
          ]},
        ]} />

        {/* Settings button */}
        <button
          onClick={() => setShowSettings(!showSettings)}
          style={{
            ...toggleBtnStyle(showSettings),
            fontSize: 18,
            padding: '0 8px',
            lineHeight: 1,
          }}
        >
          ⚙️
        </button>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div
          style={{
            padding: '8px 20px',
            borderBottom: '1px solid var(--border-subtle)',
            background: 'var(--bg-surface)',
            display: 'flex',
          }}
        >
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)' }}>
                EWT target
              </span>
              <input
                type="number"
                step="0.1"
                min="0"
                value={ewtThreshold}
                onChange={(e) => setEwtThreshold(Math.max(0, parseFloat(e.target.value) || 0))}
                style={{
                  width: 50,
                  padding: '2px 4px',
                  fontSize: 10,
                  borderRadius: 4,
                  border: '1px solid var(--border)',
                  background: 'var(--bg-elevated)',
                  color: 'var(--text-primary)',
                }}
              />
              <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>m</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)' }}>
                OTP target
              </span>
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={otpThreshold}
                onChange={(e) => setOtpThreshold(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                style={{
                  width: 50,
                  padding: '2px 4px',
                  fontSize: 10,
                  borderRadius: 4,
                  border: '1px solid var(--border)',
                  background: 'var(--bg-elevated)',
                  color: 'var(--text-primary)',
                }}
              />
              <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>%</span>
            </div>
          </div>
        </div>
      )}

      {/* Scrollable table */}
      <div ref={tableWrapRef} className="table-wrapper" style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
        <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={stickyFirstColStyle}>Hour</th>
              <th style={stickySecondColStyle}>Hour Metric</th>
              {timingPoints.map(stop => {
                const directionColor = stop.direction === 'outbound' ? 'var(--ob-track)' : 'var(--ib-track)';
                return (
                  <th key={`${stop.naptan_id}-${stop.direction}`} style={stopThStyle}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: directionColor }}>
                      {stop.direction}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3 }}>
                      {stop.name}
                    </div>
                    <div style={{ fontSize: 9, fontFamily: "'JetBrains Mono'", color: 'var(--text-muted)', marginTop: 3 }}>
                      {stop.timing_point_code}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {/* Daily summary row (route-level only) */}
            {dailyAggregates && (
              <tr style={{ background: 'var(--bg-highlight)', borderBottom: '2px solid var(--border)' }}>
                <td style={{ ...stickyFirstTdStyle('var(--bg-highlight)'), fontWeight: 700 }}>
                  Daily
                </td>
                <td style={{ ...stickySecondTdStyle('var(--bg-highlight)') }}>
                  {renderMetricValue(dailyAggregates.ewt, dailyAggregates.otp)}
                </td>
                {timingPoints.map(stop => (
                  <td key={`${stop.naptan_id}-${stop.direction}`} style={{ ...bodyCellStyle, background: 'var(--bg-highlight)' }} />
                ))}
              </tr>
            )}

            {/* Hourly rows */}
            {hours.map(hour => {
              const rowBg = hour % 2 === 0 ? 'var(--bg-surface)' : 'var(--bg-elevated)';
              return (
                <tr key={hour} style={{ background: rowBg }}>
                  <td style={stickyFirstTdStyle(rowBg)}>{hour}</td>
                  <td style={stickySecondTdStyle(rowBg)}>
                    {renderHourMetric(hour)}
                  </td>
                  {timingPoints.map(stop => (
                    <td key={`${stop.naptan_id}-${stop.direction}-${hour}`} style={bodyCellStyle}>
                      {renderStopMetric(hour, stop)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}